import { setConfig, getConfig } from './core/config';
import type { BugReporterConfig } from './core/config';
import { setMetadata, addContext } from './core/state';
import { startConsoleInterceptor, stopConsoleInterceptor } from './collectors/console';
import { startNetworkInterceptor, stopNetworkInterceptor } from './collectors/network';
import { startErrorListener, stopErrorListener } from './collectors/errors';
import { startPerformanceCollector, stopPerformanceCollector } from './collectors/performance';
import { createWidget, destroyWidget, openPanel, closePanel } from './ui/panel';

let initialized = false;

export const BugReporter = {
  init(config: Partial<BugReporterConfig> = {}): void {
    if (initialized) {
      console.warn('[BugReporter] Already initialized. Call destroy() first.');
      return;
    }

    setConfig(config);
    initialized = true;

    // Start collectors immediately
    startConsoleInterceptor();
    startNetworkInterceptor();
    startErrorListener();
    startPerformanceCollector();

    // Create the widget UI
    createWidget();

    console.log('[BugReporter] Initialized', getConfig().app ? `for app: ${getConfig().app}` : '');
  },

  open(): void {
    if (!initialized) {
      console.warn('[BugReporter] Not initialized. Call init() first.');
      return;
    }
    openPanel();
  },

  close(): void {
    if (!initialized) return;
    closePanel();
  },

  setMetadata(meta: Record<string, unknown>): void {
    setMetadata(meta);
  },

  addContext(key: string, value: unknown): void {
    addContext(key, value);
  },

  destroy(): void {
    if (!initialized) return;
    initialized = false;

    stopConsoleInterceptor();
    stopNetworkInterceptor();
    stopErrorListener();
    stopPerformanceCollector();
    destroyWidget();

    console.log('[BugReporter] Destroyed');
  },
};

// Expose globally
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).BugReporter = BugReporter;

  // Auto-init from script tag
  const run = (): void => {
    if (initialized) return;
    const el = document.querySelector('script[data-api-key]') as HTMLScriptElement
      || document.querySelector('script[data-key]') as HTMLScriptElement;
    if (!el) return;
    const apiKey = el.getAttribute('data-api-key') || el.getAttribute('data-key') || '';
    const endpoint = el.getAttribute('data-endpoint') || '';
    const app = el.getAttribute('data-app') || '';
    if (apiKey || app) {
      const c: Partial<BugReporterConfig> = { apiKey, app };
      if (endpoint) c.endpoint = endpoint;
      BugReporter.init(c);
    }
  };

  // Run when DOM is ready, with retries for SPAs
  const start = (): void => {
    run();
    if (!initialized) {
      let n = 0;
      const t = setInterval(() => { run(); if (initialized || ++n > 20) clearInterval(t); }, 500);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

export default BugReporter;
